import { useState, useEffect } from "react";
import useApi from "./useApi";

export interface AgentData {
  userId: number;
  displayName: string;
  status: string;
  currentChats: number;
  maxChats: number;
}

export interface TeamData {
  teamId: number;
  teamName: string;
  agentsOnline: number;
  agentsAvailable: number;
  agentsBusy: number;
  openConversations: number;
  unassignedConversations: number;
  longestWaitMinutes: number;
  avgWaitMinutes: number;
  /** "covered" | "no_coverage" | "no_agents" */
  coverageStatus: string;
  agents: AgentData[];
}

export interface DashboardKpis {
  agentsOnline: number;
  agentsAvailable: number;
  agentsBusy: number;
  openConversations: number;
  queuedConversations: number;
  botActive: number;
  resolvedToday: number;
  missedAbandonedToday: number;
  longestWaitMinutes: number;
  avgWaitMinutes: number;
  avgHandleTimeMinutes: number;
  avgFirstResponseMinutes: number;
}

export interface TopAgent {
  name: string;
  resolved: number;
  active: number;
}

export interface ChannelBreakdown {
  channel: string;
  count: number;
  queued: number;
}

export interface DashboardData {
  isManagerView: boolean;
  viewingTeamIds: number[];
  kpis: DashboardKpis;
  topAgents: TopAgent[];
  channelBreakdown: ChannelBreakdown[];
  teams: TeamData[];
}

export type StreamStatus = "connecting" | "live" | "reconnecting" | "error";

export function useLiveDashboard() {
  const api = useApi();
  const [data, setData] = useState<DashboardData | null>(null);
  const [streamStatus, setStreamStatus] = useState<StreamStatus>("connecting");
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    let aborted = false;
    let retryDelay = 2000;

    async function connect() {
      setStreamStatus("connecting");
      try {
        // Use fetch() so we can pass Bearer auth header — native EventSource can't
        const resp = await api.admin.get("LiveDashboard/stream");

        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }

        const reader = resp.body!.getReader();
        const decoder = new TextDecoder();
        setStreamStatus("live");
        retryDelay = 2000; // reset on successful connect

        let buffer = "";

        while (!aborted) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // SSE lines are terminated by \n\n — process complete events
          const parts = buffer.split("\n\n");
          buffer = parts.pop() ?? ""; // last part may be incomplete

          for (const part of parts) {
            for (const line of part.split("\n")) {
              if (line.startsWith("data: ")) {
                try {
                  const parsed: DashboardData = JSON.parse(line.slice(6));
                  setData(parsed);
                  setLastUpdated(new Date());
                } catch {
                  // Ignore malformed SSE data
                }
              }
            }
          }
        }
      } catch {
        if (!aborted) {
          setStreamStatus("reconnecting");
          await new Promise<void>((r) => setTimeout(r, retryDelay));
          retryDelay = Math.min(retryDelay * 2, 30000); // cap at 30s
          if (!aborted) connect();
        }
      }
    }

    connect();
    return () => {
      aborted = true;
    };
  }, [api]);

  return { data, streamStatus, lastUpdated };
}
